import { EvaluationLocation } from "./evaluation-location.util";
import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, Subject, of } from "rxjs";
import { map, tap } from "rxjs/operators";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";
import { AuthService } from "../auth/authServices";
import { ConfigService } from "src/app/services/config.service";
import { sortUsersByFullName } from "src/app/utils/sort-users";
import { pickerToUtcDateString } from "../pi/pi.service";

@Injectable({
  providedIn: "root",
})
export class EvalService {
  private apiUrl: string;

  /** Mat-datepicker day → YYYY-MM-DD (avoids local-midnight → previous UTC day). */
  private formatTypeDate(typeDate: unknown): string {
    if (typeDate == null || typeDate === "") {
      return "";
    }
    if (typeDate instanceof Date) {
      return pickerToUtcDateString(typeDate) || "";
    }
    return String(typeDate);
  }

  private currentOccurrenceId(): string | null {
    try {
      const fromSearch = new URLSearchParams(window.location.search).get(
        "occurrenceId"
      );
      if (fromSearch) {
        return fromSearch;
      }
      // Angular hash routing: /#/path?id=…&occurrenceId=…
      const hash = String(window.location.hash || "");
      const q = hash.indexOf("?");
      if (q >= 0) {
        return new URLSearchParams(hash.slice(q + 1)).get("occurrenceId");
      }
      return null;
    } catch {
      return null;
    }
  }

  private appendOccurrenceId(
    formData: FormData,
    occurrenceIdOverride?: string | null
  ): void {
    const occ =
      (occurrenceIdOverride && String(occurrenceIdOverride).trim()) ||
      this.currentOccurrenceId();
    if (occ) {
      formData.append("occurrenceId", occ);
    }
  }
  /** One-shot flag consumed by the next updateEvaluation FormData post. */
  private pendingInterpreterDetailsConfirmed: boolean | undefined;
  /** One-shot PI "Not Completed" reason consumed by the next updateEvaluation FormData post. */
  private pendingNotCompletedReason: string | undefined;
  private historyStatusSource = new BehaviorSubject<string>(null);
  historyStatus$ = this.historyStatusSource.asObservable();

  private recordStatusSource = new BehaviorSubject<string>(null);
  recordStatus$ = this.recordStatusSource.asObservable();

  /** Mirrors EncounterReport.allRecordsUploads for tab icon / banner (no records received path). */
  private recordAllRecordsUploadsSource = new BehaviorSubject<boolean>(false);
  recordAllRecordsUploads$ = this.recordAllRecordsUploadsSource.asObservable();

  private editorStatusSource = new BehaviorSubject<string>(null);
  editorStatus$ = this.editorStatusSource.asObservable();

  private EvaluationStatusSource = new BehaviorSubject<string>(null);
  evaluationStatus$ = this.EvaluationStatusSource.asObservable();

  private FinalizationStatusSource = new BehaviorSubject<string>(null);
  finalizationStatus$ = this.FinalizationStatusSource.asObservable();

  private DepostionStatusSource = new BehaviorSubject<string>(null);
  deposition_status$ = this.DepostionStatusSource.asObservable();

  private SubmitStatusSource = new BehaviorSubject<string>(null);
  report_submission_status$ = this.SubmitStatusSource.asObservable();

  private phoneCallStatusSource = new BehaviorSubject<string>(null);
  phone_call_status$ = this.phoneCallStatusSource.asObservable();

  private courtAppearanceStatusSource = new BehaviorSubject<string>(null);
  court_appearance_status$ = this.courtAppearanceStatusSource.asObservable();

  private depositionCourtStatusSource = new BehaviorSubject<string>(null);
  deposition_court_status$ = this.depositionCourtStatusSource.asObservable();

  private physicianRecordReviewStatusSource = new BehaviorSubject<string>(null);
  physician_record_review_status$ =
    this.physicianRecordReviewStatusSource.asObservable();

  private encounterReloadSource = new Subject<string>();
  encounterReload$ = this.encounterReloadSource.asObservable();
  private shellReloadSource = new Subject<string>();
  shellReload$ = this.shellReloadSource.asObservable();
  /** In-memory report payload so shell + active tab share one GET. */
  private encounterReportCache = new Map<string, unknown>();

  constructor(
    private http: HttpClient,
    private router: Router,
    private toastr: ToastrService,
    private Auth: AuthService,
    private configService: ConfigService,
  ) {
    this.apiUrl = this.configService.get('apiUrl') + '/admin';
  }

  updateHistoryStatus(status: string) {
    this.historyStatusSource.next(status);
  }

  updateRecordStatus(status: string) {
    this.recordStatusSource.next(status);
  }

  updateRecordAllRecordsUploads(allRecordsUploads: boolean) {
    this.recordAllRecordsUploadsSource.next(!!allRecordsUploads);
  }

  updateEditorStatus(status: string) {
    this.editorStatusSource.next(status);
  }

  updateEvaluationStatus(status: string) {
    this.EvaluationStatusSource.next(status);
  }

  updateFinalizationStatus(status: string) {
    this.FinalizationStatusSource.next(status);
  }

  updateDepostionStatus(status: string) {
    this.DepostionStatusSource.next(status);
  }

  updateSubmitStatus(status: string) {
    this.SubmitStatusSource.next(status);
  }

  updatePhoneCallStatus(status: string) {
    this.phoneCallStatusSource.next(status);
  }

  notifyEncounterReload(encounterId: string): void {
    if (encounterId) {
      this.encounterReloadSource.next(encounterId);
    }
  }

  /** Reload eval shell report (tab icons / piServices) after per-occurrence complete. */
  requestShellDataReload(encounterId: string): void {
    if (encounterId) {
      this.invalidateEncounterReportCache(encounterId);
      this.shellReloadSource.next(encounterId);
    }
  }

  invalidateEncounterReportCache(encounterId?: string): void {
    if (encounterId) {
      this.encounterReportCache.delete(encounterId);
    } else {
      this.encounterReportCache.clear();
    }
  }

  updateCourtAppearanceStatus(status: string) {
    this.courtAppearanceStatusSource.next(status);
  }

  updateDepositionCourtStatus(status: string) {
    this.depositionCourtStatusSource.next(status);
  }

  updatePhysicianRecordReviewStatus(status: string) {
    this.physicianRecordReviewStatusSource.next(status);
  }

  getEncounterReportData(
    id: string,
    options?: { force?: boolean }
  ): Observable<any> {
    if (!options?.force && this.encounterReportCache.has(id)) {
      return of({
        status: "success",
        data: this.encounterReportCache.get(id),
      });
    }
    return this.http.get<any>(`${this.apiUrl}/getEncounterData/${id}`, {}).pipe(
      tap((response) => {
        if (response?.data) {
          this.encounterReportCache.set(id, response.data);
        }
      })
    );
  }

  ensureApplicantDemographics(encounterId: string): Observable<any> {
    return this.http
      .post<any>(
        `${this.apiUrl}/ensureApplicantDemographics/${encounterId}`,
        {}
      )
      .pipe(
        tap((response) => {
          const demoFileId = response?.data?.demoFileId;
          const existing = this.encounterReportCache.get(encounterId);
          if (!demoFileId || existing == null || typeof existing !== "object") {
            return;
          }
          this.encounterReportCache.set(encounterId, {
            ...(existing as Record<string, unknown>),
            demoFileId,
            demoFileName: response.data.demoFileName,
          });
        })
      );
  }

  getAllDueDates(): Observable<any> {
    const roleName = this.Auth.getRoleName();
    const roleQuery = roleName
      ? `&roleName=${encodeURIComponent(roleName)}`
      : "";
    return this.http.get<any>(
      `${this.apiUrl}/get_dueDates?name=Get All Due Dates${roleQuery}`
    );
  }

  getPersonaList(
    personas: string[],
    preferredLanguage?: string,
    fallbackToEnglish: boolean = false
  ): Observable<any> {
    const personasParam = encodeURIComponent(personas.join(","));
    const queryParams: string[] = [`personas=${personasParam}`];
    const languageAwareRoles = new Set(["HISTORIAN", "HISTORIAN MANAGER"]);
    const needsExtendedFields =
      Boolean(preferredLanguage) ||
      personas.length > 1 ||
      personas.some((role) => languageAwareRoles.has(role));

    if (!needsExtendedFields) {
      queryParams.push("minimal=true");
    }
    if (preferredLanguage) {
      queryParams.push(`preferredLanguage=${encodeURIComponent(preferredLanguage)}`);
    }
    if (fallbackToEnglish) {
      queryParams.push("fallbackToEnglish=true");
    }
    return this.http
      .get<any>(`${this.apiUrl}/getPersonas/?${queryParams.join("&")}`, {})
      .pipe(
        map((response) => ({
          ...response,
          data: sortUsersByFullName(response?.data),
        }))
      );
  }

  updateApplicantInfo(id: string, data: any) {
  return this.http.post(`${this.apiUrl}/update_applicant_encounter/${id}`, data);
}

  updateHistory(
    reqUpdatePhone: any,
    assignToHistorian: string,
    history_status: string,
    historyDateTime: any,
    dictation: string,
    injuryDetails: any,
    historyFiles: File[],
    id: any
  ): Observable<any> {
    const formData = new FormData();
    formData.append("reqUpdatePhone", reqUpdatePhone);
    formData.append("assignToHistorian", assignToHistorian);
    formData.append("history_status", history_status);
    formData.append("historyDateTime", historyDateTime);
    formData.append("dictation", dictation);
    formData.append("injuryDetails", JSON.stringify(injuryDetails));

    historyFiles.forEach((file, index) => {
      formData.append(`historyFiles`, file, file.name);
    });
    this.appendOccurrenceId(formData);

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageHistory/${id}`,
      formData
    );
  }

  // ScheduledHistory(historyDateTime: any, id: any): Observable<any> {
  //   return this.http.post<{ message: string; data: any }>(
  //     `${this.apiUrl}/scheduledHistory/${id}`,
  //     { historyDateTime }
  //   );
  // }

  updateRecordReview(
    assignToRecord: string,
    record_status: string,
    recordReviewFiles: File[],
    allRecordsUploads: any,
    id: any
  ): Observable<any> {
    const formData = new FormData();

    formData.append("assignToRecord", assignToRecord);
    formData.append("record_status", record_status);
    formData.append("allRecordsUploads", allRecordsUploads);

    if (recordReviewFiles && recordReviewFiles.length > 0) {
      recordReviewFiles.forEach((file) => {
        formData.append("recordReviewFiles", file, file.name);
      });
    }

    this.appendOccurrenceId(formData);
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageRecordReview/${id}`,
      formData
    );
  }

  updateMultipleRecordPageCounts(
    records: any,
    id: any
  ): Observable<any> {
    console.log(id , records)
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageUpdateRecordPageCount/${id}`,
      { records, occurrenceId: this.currentOccurrenceId() || undefined }
    );
  }

  ManagePQMES(allRecordsUploads: any, id: any, occurrenceId?: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/managePQMES/${id}`,
      { allRecordsUploads, occurrenceId }
    );
  }

  updateForm(type: string, file: File, id: any): Observable<any> {
    const formData = new FormData();
    formData.append("type", type);
    formData.append("file", file);
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageForm/${id}`,
      formData
    );
  }

  checkExitCode(payload: {
    exitCode: string;
  }): Observable<{ success: boolean; exitCodeExists: boolean }> {
    return this.http.post<{ success: boolean; exitCodeExists: boolean }>(
      `${this.apiUrl}/checkExistCode`,
      payload
    );
  }

  /**
   * Personal Injury Editing — assignment.
   * The WC /manageEditor route rejects PI (report.controller.js guards it and
   * points here), so assignment is occurrence-scoped on the PI router.
   * Sent as JSON, not FormData: /pi_report_assign_editor has no multer, so a
   * multipart body would arrive empty. editor_status is derived server-side.
   */
  assignPiReportEditor(
    assignToEditor: string,
    id: any,
    occurrenceId?: string | null
  ): Observable<{ status: string; data: any }> {
    return this.http.post<{ status: string; data: any }>(
      `${this.apiUrl}/pi_report_assign_editor/${id}`,
      {
        assignToEditor: assignToEditor || null,
        occurrenceId: occurrenceId || this.currentOccurrenceId() || undefined,
      }
    );
  }

  /**
   * Personal Injury Editing / Report — single-file upload for one occurrence.
   * Multipart here is correct: the route is wired with upload.single("file").
   */
  uploadPiReportFile(
    file: File,
    tabKey: "editing" | "report" | "submission" | "finalization",
    id: any,
    occurrenceId?: string | null
  ): Observable<{ success: boolean; data: any }> {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tabKey", tabKey);
    this.appendOccurrenceId(formData, occurrenceId);
    const occ =
      (occurrenceId && String(occurrenceId).trim()) ||
      this.currentOccurrenceId() ||
      "x";
    const idempotencyKey = [
      "pi-edit",
      occ,
      file.name,
      String(file.size),
      String(file.lastModified),
    ]
      .join("-")
      .slice(0, 200);
    return this.http.post<{ success: boolean; data: any }>(
      `${this.apiUrl}/pi_report_upload/${id}`,
      formData,
      { headers: new HttpHeaders({ "Idempotency-Key": idempotencyKey }) }
    );
  }

  updateEditorReports(
    assignToEditor: string,
    editor_status: string,
    time_spent_reviewing_sub_rosa: any,
    uploadFinalReportFileName: File,
    id: any
  ): Observable<any> {
    const formData = new FormData();

    formData.append("assignToEditor", assignToEditor);
    formData.append("editor_status", editor_status);
    formData.append(
      "time_spent_reviewing_sub_rosa",
      time_spent_reviewing_sub_rosa
    );
    formData.append("uploadFinalReportFileName", uploadFinalReportFileName);
    // editorFiles.forEach((file, index) => {
    //   formData.append(`editorFiles`, file, file.name);
    // });

    this.appendOccurrenceId(formData);
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageEditor/${id}`,
      formData
    );
  }

  updateDepostionReports(
    deposition_status: string,
    deposition_online: any,
    deposition_meetLink: any,
    deposition_addressLine_1: string,
     deposition_addressLine_2: string,
    deposition_city: string,
    deposition_state: string,
    deposition_zip: string,
    deposition_paymentRecive: any,
    depositionFileName: File,
    id: any,
    occurrenceId?: string
  ): Observable<any> {
    const formData = new FormData();

    if (occurrenceId) {
      formData.append("occurrenceId", occurrenceId);
    }
    // Append all required fields to the FormData object
    formData.append("deposition_status", deposition_status);
    formData.append("deposition_online", deposition_online);
    formData.append("deposition_meetLink", deposition_meetLink);
    formData.append("deposition_addressLine_1", deposition_addressLine_1);
     formData.append("deposition_addressLine_2", deposition_addressLine_2);
    formData.append("deposition_city", deposition_city);
    formData.append("deposition_state", deposition_state);
    formData.append("deposition_zip", deposition_zip);
    formData.append("deposition_paymentRecive", deposition_paymentRecive);

    if (depositionFileName) {
      formData.append("depositionFileName", depositionFileName);
    }

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/depositionReport/${id}`,
      formData
    );
  }

  submitReport(dataSource: any, id: any, reportDatedOn: string): Observable<any> {
    let payload = {
      data: dataSource,
      reportDatedOn,
    };

    const occurrenceId = this.currentOccurrenceId();
    if (occurrenceId) {
      (payload as { occurrenceId?: string }).occurrenceId = occurrenceId;
    }

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/submitReport/${id}`,
      payload
    );
  }

  submitNoCover(id: any): Observable<any> {

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/sentNoCoverToBiller/${id}`,
      {}
    );
  }

  updateFinalizationReports(
    assignToReportFinalizer: string,
    finalization_status: string,
    uploadFinalizationReportFileName: File,
    // uploadFinalizationReports: File[],
    id: any,
    occurrenceId?: string
  ): Observable<any> {
    const formData = new FormData();

    formData.append("assignToReportFinalizer", assignToReportFinalizer);
    formData.append("finalization_status", finalization_status);
    formData.append(
      "uploadFinalizationReportFileName",
      uploadFinalizationReportFileName
    );
    if (occurrenceId) {
      formData.append("occurrenceId", occurrenceId);
    }
    // uploadFinalizationReports.forEach((file, index) => {
    //   formData.append(`uploadFinalizationReports`, file, file.name);
    // });

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageFinalization/${id}`,
      formData
    );
  }

  updateEvaluation(
    assignToMedicalAssistant: any,
    applicantAttendance: any,
    timely_cancellation: any,
    interpreterAttendance: any,
    applicantArrived: any,
    interpreterArrived: any,
    interpreterFullyCertified: any,
    evaluation_status: any,
    faceToFace: any,
    time_spent_reviewing_sub_rosa: any,
    applicantAttendance_2: any,
    interpreterAttendance_2: any,
    cancelDate: any,
    cancelBy: any,
    comment: any,
    reason_unknown: any,
    comment_2: any,
    time: any,
    comment_3: any,
    uploadPateintForms: File[],
    uploadPatientId: File[],
    uploadTemplate: File[],
    uploadDictaion: File[],
    reschedule: any,
    id: any,
    evaluationLocation?: EvaluationLocation
  ): Observable<any> {
    const formData = new FormData();
    if (evaluationLocation) {
      for (const key of ["evaluationMode", "remoteEvaluationMeetingUrl", "evaluationDurationMinutes", "examinationAddress"] as const) {
        formData.append(key, String(evaluationLocation[key] ?? ""));
      }
    }

    formData.append("assignToMedicalAssistant", assignToMedicalAssistant);
    formData.append("applicantAttendance", applicantAttendance);
    formData.append("timely_cancellation", timely_cancellation);
    formData.append("interpreterAttendance", interpreterAttendance);
    formData.append("applicantArrived", applicantArrived);
    formData.append("interpreterArrived", interpreterArrived);
    formData.append("interpreterFullyCertified", interpreterFullyCertified);
    formData.append("evaluation_status", evaluation_status);
    formData.append("faceToFace", faceToFace);
    formData.append(
      "time_spent_reviewing_sub_rosa",
      time_spent_reviewing_sub_rosa
    );
    formData.append(
      "applicantAttendance_2",
      applicantAttendance_2
    );
    formData.append(
      "interpreterAttendance_2",
      interpreterAttendance_2
    );
    formData.append(
      "cancelDate",
      cancelDate
    );
    formData.append(
      "cancelBy",
      cancelBy
    );
    formData.append(
      "comment",
      comment
    );
    formData.append(
      "reason_unknown",
      reason_unknown
    );
    formData.append(
      "comment_2",
      comment_2
    );
      formData.append(
      "time",
      time
    );
      formData.append(
      "comment_3",
      comment_3
    );
    uploadPateintForms.forEach((file, index) => {
      formData.append(`uploadPateintForms`, file, file.name);
    });
    uploadPatientId.forEach((file) => {
      formData.append(`uploadPatientId`, file, file.name);
    });
    uploadTemplate.forEach((file, index) => {
      formData.append(`uploadTemplate`, file, file.name);
    });
    uploadDictaion.forEach((file, index) => {
      formData.append(`uploadDictaion`, file, file.name);
    });
    formData.append("reschedule", reschedule);
    if (this.pendingInterpreterDetailsConfirmed !== undefined) {
      formData.append(
        "interpreterDetailsConfirmed",
        String(!!this.pendingInterpreterDetailsConfirmed)
      );
      this.pendingInterpreterDetailsConfirmed = undefined;
    }
    if (this.pendingNotCompletedReason !== undefined) {
      formData.append("notCompletedReason", this.pendingNotCompletedReason);
      this.pendingNotCompletedReason = undefined;
    }
    this.appendOccurrenceId(formData);
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageEvaluation/${id}`,
      formData
    );
  }

  /** Set before updateEvaluation for PI Evaluation "Not Completed" reason (keeps updateEvaluation arity stable). */
  setPendingNotCompletedReason(reason: string): void {
    this.pendingNotCompletedReason = reason || "";
  }

  /** Set before updateEvaluation for PI interpreter confirm (keeps updateEvaluation arity stable). */
  setPendingInterpreterDetailsConfirmed(confirmed: boolean): void {
    this.pendingInterpreterDetailsConfirmed = confirmed;
  }

  URI(
    uriSymptoms: any,
    uriSymptomsNone: any,
    uriSignFileName: any,
    generalInformationStatus: any,
    regulation40: any,
    regulationSignFileName: any,
    // form100FileName: any,
    id: any
  ): Observable<any> {
    // const formData = new FormData();

    let data = {
      uriSymptoms,
      uriSymptomsNone,
      uriSignFileName,
      generalInformationStatus,
      regulation40,
      regulationSignFileName,
    };

    // formData.append("uriSymptoms", uriSymptoms);
    // formData.append("uriSignFileName", uriSignFileName);
    // formData.append("generalInformationStatus", generalInformationStatus);
    // formData.append("regulation40", regulation40);
    // formData.append("regulationSignFileName", regulationSignFileName);
    // // formData.append("form100FileName", form100FileName);

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/evaluationForApplicant/${id}`,
      data
    );
  }

  interpreterForm(
    interpreterURISymptoms: any,
    interpreteruriSymptomsNone: any,
    interpreterURISignFileName: any,
    firstName: any,
    middleName: any,
    lastName: any,
    certificateNumber: any,
    certificateType: any,
    expiryDate: any,
    certificate: any,
    id: any
  ): Observable<any> {
    const formData = new FormData();

    formData.append("interpreterURISymptoms", interpreterURISymptoms);
    formData.append("interpreteruriSymptomsNone", interpreteruriSymptomsNone);
    formData.append("interpreterURISignFileName", interpreterURISignFileName);
    formData.append("firstName", firstName);
    formData.append("middleName", middleName);
    formData.append("lastName", lastName);
    formData.append("certificateNumber", certificateNumber);
    formData.append("certificateType", certificateType);
    formData.append("expiryDate", expiryDate);
    formData.append("certificate", certificate);

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/evaluationForEnterpreter/${id}`,
      formData
    );
  }

  vitalsForm(formData: any, id: any): Observable<any> {
    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/vitalsFrom/${id}`,
      formData
    );
  }

  updateRecord(
    senderName: string,
    sender: string,
    type: any,
    typeDate: any,
    attestationPageCount: any,
    invalid: any,
    recordFile: File,
    id: any,
    senderCode?: string,
    forceNewRow?: boolean,
    gDriveUrl: string = null,
    pageCount: number = 0,
    occurrenceId?: string | null,
  ): Observable<any> {
    const formData = new FormData();
    formData.append("senderName", senderName || '');
    formData.append("sender", sender || '');
    formData.append("type", type || '');
    formData.append("typeDate", this.formatTypeDate(typeDate));
    formData.append("attestationPageCount", attestationPageCount != null ? String(attestationPageCount) : '');
    formData.append("invalid", invalid != null ? String(invalid) : 'false');
    formData.append("gDriveUrl", gDriveUrl || '');
    formData.append("pageCount", pageCount != null ? String(pageCount) : '0');
    if (recordFile) {
      formData.append(`recordFile`, recordFile);
    }
    if (senderCode) {
      formData.append("senderCode", senderCode);
    }
    if (typeof forceNewRow !== 'undefined') {
      formData.append("forceNewRow", String(forceNewRow));
    }
    this.appendOccurrenceId(formData, occurrenceId);

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageRecord/${id}`,
      formData
    );
  }

  updateRecordByID(
    senderName: string,
    sender: string,
    type: any,
    typeDate: any,
    attestationPageCount: any,
    invalid: any,
    recordFile: File,
    id: any,
    recordId: any,
    senderCode?: string,
    forceNewRow?: boolean,
    gDriveUrl: string = null,
    pageCount: number = 0,
    occurrenceId?: string | null,
  ): Observable<any> {
    const formData = new FormData();
    formData.append("senderName", senderName || '');
    formData.append("sender", sender || '');
    formData.append("type", type || '');
    formData.append("typeDate", this.formatTypeDate(typeDate));
    formData.append("attestationPageCount", attestationPageCount != null ? String(attestationPageCount) : '');
    formData.append("invalid", invalid != null ? String(invalid) : 'false');
    formData.append("gDriveUrl", gDriveUrl || '');
    formData.append("pageCount", pageCount != null ? String(pageCount) : '0');
    if (recordFile) {
      formData.append(`recordFile`, recordFile);
    }
    if (senderCode) {
      formData.append("senderCode", senderCode);
    }
    if (typeof forceNewRow !== 'undefined') {
      formData.append("forceNewRow", String(forceNewRow));
    }
    this.appendOccurrenceId(formData, occurrenceId);

    return this.http.post<{ message: string; data: any }>(
      `${this.apiUrl}/manageUpdateRecord/${id}/${recordId}`,
      formData
    );
  }

  deleteFile(
    encounterId: string,
    fileId: string,
    type: string,
    uniqueFileId?: string
  ): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    const body: { uniqueFileId?: string; occurrenceId?: string } = {
      uniqueFileId,
    };
    if (occurrenceId) {
      body.occurrenceId = occurrenceId;
    }
    return this.http.post<any>(
      `${this.apiUrl}/delete_file/${encounterId}/${fileId}/${type}`,
      body
    );
  }

  unAssignReq(id: string, receiverId: string, type: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/unassignReqNotification/${id}/${receiverId}/${type}`,
      {}
    );
  }

  unAssignHistorian(id: string): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/unassignHistorian/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  unAssignRecordReview(id: string): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/unassignRecordReview/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  unAssignEditor(id: string): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/unassignEditor/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  unAssignMedicalAssistant(id: string): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/unassignMedicalAssistant/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  unAssignFinalization(id: string): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/unassignFinalization/${id}`, {});
  }

  markToCompleteHistory(id: any): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/markHistory/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  markToCompleteRecord(id: any): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/markToRecord/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  markToCompleteEvaluation(id: any, type: string): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    const payload: { type: string; occurrenceId?: string } = { type };
    if (occurrenceId) {
      payload.occurrenceId = occurrenceId;
    }
    return this.http.post<any>(`${this.apiUrl}/markasEvaluation/${id}`, payload);
  }

  markToCompleteEditing(id: any): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    return this.http.post<any>(
      `${this.apiUrl}/marskAsEditing/${id}`,
      occurrenceId ? { occurrenceId } : {}
    );
  }

  genrateReport(id: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/genrateReport/${id}`, {});
  }

  genrateUpdatedReport(id: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/genrateUpdatedReport/${id}`, {});
  }

  markToCompleteFinalization(id: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/markAsFinalization/${id}`, {});
  }

    markToCompleteDeposition(id: any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/marskAsDepositionReport/${id}`, {});
  }

  savePiPhysicianRecordReviewDraft(
    encounterId: string,
    payload: {
      minutes?: number | string;
      note?: string;
      durationHours?: number | string;
      durationMinutes?: number | string;
      durationSeconds?: number | string;
      occurrenceId?: string;
    }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_physician_record_review_draft/${encounterId}`,
      payload
    );
  }

  markCompletePiPhysicianRecordReview(
    encounterId: string,
    payload?: { occurrenceId?: string }
  ): Observable<{ success: boolean; data: unknown }> {
    const roleName = this.Auth.getRoleName();
    const roleQuery = roleName ? `?roleName=${encodeURIComponent(roleName)}` : "";
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/markCompletePiPhysicianRecordReview/${encounterId}${roleQuery}`,
      payload ?? {}
    );
  }

  uploadPiPhysicianRecordReviewMemo(
    encounterId: string,
    formData: FormData
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.apiUrl}/pi_physician_record_review_upload/${encounterId}`,
      formData
    );
  }

  // sent To request for update phone number

    reqToUpdatePhoneNumber(id: any, reqUpdatePhone:any): Observable<any> {
    return this.http.post<any>(`${this.apiUrl}/requestUpdatePhoneNumber/${id}`, {reqUpdatePhone:reqUpdatePhone});
  }

  updateFileName(
    encounterId: string,
    fileId: string,
    newName: string,
    type: string
  ): Observable<any> {
    const occurrenceId = this.currentOccurrenceId();
    const body: { newName: string; occurrenceId?: string } = { newName };
    if (occurrenceId) {
      body.occurrenceId = occurrenceId;
    }
    return this.http.post<any>(
      `${this.apiUrl}/update_file/${encounterId}/${fileId}/${type}`,
      body
    );
  }
}
